import React from 'react';
import { Callout, DirectionalHint, getTheme, Icon, IconButton, SearchBox, Spinner, SpinnerSize } from '@fluentui/react';
import { IStorefrontSection } from '../../../../models/Storefront';
import { IStorefrontProductView } from '../../../../models/StorefrontProducts';
import { useLazySearchAdminStorefrontProductsQuery } from '../../../../store/endpoints/StorefrontSettingsEndpoints';
import { PickThumb } from './ContentSectionForms';
import { useResolvedSectionProducts } from './useResolvedSectionProducts';

// ---------- Selected Products ----------
// The Basic-tab form for `selectedProducts` sections: the same debounced search-as-you-type picker
// the Single Product section uses (products only — these sections hold product ids), where each
// pick appends to section.productIds, above a removable list of the current selections.

interface ISelectedProductsFormProps {
    section: IStorefrontSection;
    onChange: (section: IStorefrontSection) => void;
    // May be a preview id (…-preview). The admin search runs against the LIVE storefront (same as
    // the Single Product picker); the section-products lookup uses the id as passed, matching the
    // preview below the section.
    storefrontId?: string;
}

const formatPrice = (n?: number): string =>
    n == null ? '' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const SelectedProductsForm: React.FC<ISelectedProductsFormProps> = ({ section, onChange, storefrontId }) => {
    const theme = getTheme();
    const liveStorefrontId = (storefrontId ?? '').replace(/-preview$/, '');
    const productIds = section.productIds ?? [];

    const [searchProducts] = useLazySearchAdminStorefrontProductsQuery();
    const [term, setTerm] = React.useState('');
    const [open, setOpen] = React.useState(false);
    const [searching, setSearching] = React.useState(false);
    const [results, setResults] = React.useState<IStorefrontProductView[]>([]);
    // Display info for ids added this session — the search hit rows we already have, seeded into
    // the shared resolver so no refetch is needed for them.
    const [pickCache, setPickCache] = React.useState<Record<string, IStorefrontProductView>>({});
    const debounceRef = React.useRef<number>();
    const anchorRef = React.useRef<HTMLDivElement>(null);

    // Shared with the section's WYSIWYG preview: saved baseline + per-id detail fetches, so ids
    // from a reopened (even unsaved) section resolve to real product names here.
    const { resolve, failed, loading } = useResolvedSectionProducts(storefrontId, section, pickCache);

    const runSearch = React.useCallback(async (raw: string) => {
        const q = raw.trim();
        if (!liveStorefrontId || q.length < 2) {
            setResults([]);
            setOpen(false);
            return;
        }
        setSearching(true);
        setOpen(true);
        const conditions = { page: 1, pageSize: 8, searchTerm: q };
        const response = await searchProducts({ storefrontId: liveStorefrontId, conditions }).unwrap().catch(() => ({ items: [] as IStorefrontProductView[] }));
        setResults(response.items ?? []);
        setSearching(false);
    }, [liveStorefrontId, searchProducts]);

    const onSearchChange = (value?: string) => {
        setTerm(value ?? '');
        window.clearTimeout(debounceRef.current);
        debounceRef.current = window.setTimeout(() => runSearch(value ?? ''), 300);
    };

    React.useEffect(() => () => window.clearTimeout(debounceRef.current), []);

    const addProduct = (product: IStorefrontProductView) => {
        if (productIds.includes(product.id)) return;
        setPickCache((cache) => ({ ...cache, [product.id]: product }));
        onChange({ ...section, productIds: [...productIds, product.id] });
    };

    const removeProduct = (id: string) =>
        onChange({ ...section, productIds: productIds.filter(p => p !== id) });

    return (
        <div style={{ marginTop: 12 }}>
            <div ref={anchorRef}>
                <SearchBox
                    placeholder='Search storefront products to add…'
                    value={term}
                    onChange={(_, v) => onSearchChange(v)}
                    onClear={() => { setTerm(''); setResults([]); setOpen(false); }}
                    disabled={!liveStorefrontId}
                />
                {!liveStorefrontId && (
                    <div style={{ fontSize: 12, color: theme.palette.neutralSecondary, marginTop: 4 }}>
                        Save the storefront first to search its catalog.
                    </div>
                )}
                {open && (
                    <Callout
                        target={anchorRef}
                        onDismiss={() => setOpen(false)}
                        isBeakVisible={false}
                        gapSpace={2}
                        directionalHint={DirectionalHint.bottomLeftEdge}
                        styles={{ root: { width: (anchorRef.current?.offsetWidth ?? 380) } }}
                    >
                        {searching ? (
                            <div style={{ padding: '1em' }}><Spinner size={SpinnerSize.small} label='Searching…' labelPosition='right' /></div>
                        ) : results.length === 0 ? (
                            <div style={{ padding: '1em', color: theme.palette.neutralSecondary, fontSize: 13 }}>
                                {term.trim().length < 2 ? 'Type at least 2 characters to search.' : 'No matches found.'}
                            </div>
                        ) : (
                            <div style={{ maxHeight: 320, overflowY: 'auto', padding: '0.25em 0' }}>
                                {results.map((product) => {
                                    const added = productIds.includes(product.id);
                                    const price = product.basePrice ?? product.msrp;
                                    return (
                                        <div
                                            key={product.id}
                                            onClick={() => { if (!added) addProduct(product); }}
                                            style={{ display: 'flex', alignItems: 'center', gap: '0.5em', padding: '0.4em 0.75em', cursor: added ? 'default' : 'pointer', opacity: added ? 0.6 : 1 }}
                                            onMouseEnter={(e) => { if (!added) e.currentTarget.style.background = theme.palette.neutralLighter; }}
                                            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                                        >
                                            <PickThumb imageUrl={product.image ?? product.images?.[0]} size={32} />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{product.name || '(untitled)'}</div>
                                                {product.manufacturer && <div style={{ fontSize: 11, color: theme.palette.neutralSecondary }}>{product.manufacturer}</div>}
                                            </div>
                                            {added
                                                ? <Icon iconName='CheckMark' styles={{ root: { color: theme.palette.themePrimary } }} title='Already in this section' />
                                                : price != null && <div style={{ fontSize: 12, color: theme.palette.neutralPrimary }}>{formatPrice(price)}</div>}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </Callout>
                )}
            </div>

            {productIds.length > 0 && (
                <div style={{ marginTop: 8, border: `1px solid ${theme.palette.neutralLight}`, borderRadius: 8, padding: '0.25em 0' }}>
                    {productIds.map((id) => {
                        const product = resolve(id);
                        return (
                            <div key={id} style={{ display: 'flex', alignItems: 'center', gap: '0.5em', padding: '0.3em 0.5em' }}>
                                <PickThumb imageUrl={product?.image ?? product?.images?.[0]} size={32} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {product?.name ?? (failed(id) && !loading ? id : 'Loading…')}
                                    </div>
                                    {product?.manufacturer && <div style={{ fontSize: 11, color: theme.palette.neutralSecondary }}>{product.manufacturer}</div>}
                                </div>
                                <IconButton
                                    iconProps={{ iconName: 'Delete' }}
                                    title='Remove from section'
                                    ariaLabel='Remove from section'
                                    onClick={() => removeProduct(id)}
                                />
                            </div>
                        );
                    })}
                </div>
            )}
            <div style={{ fontSize: 12, color: '#605e5c', paddingTop: 4 }}>
                {productIds.length === 0
                    ? 'Search above to add products listed on this storefront. Products can also be added from a product’s Storefront tab.'
                    : `${productIds.length} product${productIds.length === 1 ? '' : 's'} selected.`}
                {section.maxEntries != null && section.maxEntries > 0 && productIds.length > section.maxEntries &&
                    ` Only the first ${section.maxEntries} will show (Max Number of Products to Show).`}
            </div>
        </div>
    );
};

export default SelectedProductsForm;
